import { FileUpload } from '@consigli/facade';
import {
  ExtendedFile,
  useGetPackagesByTypeQuery,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import { DocumentPackageType, PipelineName } from '@consigli/types';
import { useCallback, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ServiceIdIndexValue } from '@/routes';

import { IterationName } from '../components/iteration-name';
import { SelectLanguage } from '../components/select-language';
import { ScheduleCheckUploadInfo } from '../components/upload-info-panel';
import { useFetchValidateName } from '../components/user-input';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export const ScheduleCheckWizard = () => {
  const [formData] = useState<FieldValues>({
    iterationName: '',
    language: '',
  });

  const { t } = useTranslation();
  const [scheduleFiles, setScheduleFiles] = useState<ExtendedFile[]>([]);
  const [drawingFiles, setDrawingFiles] = useState<ExtendedFile[]>([]);
  const [schematicFiles, setSchematicFiles] = useState<ExtendedFile[]>([]);
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const { createDocumentPackage, uploadFiles, startAnalysis } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm<FieldValues>({
    defaultValues: formData,
  });

  const { getValues, handleSubmit } = methods;

  const onAnalyze = useCallback(async () => {
    if (drawingFiles.length === 0) {
      toast.error(t('package-creation.drawings-required'));
      return;
    }

    try {
      setIsProcessing(true);

      // Step 1: Create package
      const createdPackageId = await createDocumentPackage(
        type,
        getValues('iterationName'),
        getValues('language'),
        false,
      );

      if (createdPackageId === undefined) {
        return;
      }

      // Step 2: Combine all files with metadata
      const combinedFiles: (File & {
        isScheduleFile?: boolean;
        isDrawing?: boolean;
        isSchematicDrawing?: boolean;
      })[] = [];

      // Schedule file (optional)
      const modifiedScheduleFiles = scheduleFiles.map((file) => {
        const extendedFile = file as File & { isScheduleFile?: boolean };
        extendedFile.isScheduleFile = true;
        return extendedFile;
      });
      combinedFiles.push(...modifiedScheduleFiles);

      // Drawings (mandatory)
      const modifiedDrawingFiles = drawingFiles.map((file) => {
        const extendedFile = file as File & { isDrawing?: boolean };
        extendedFile.isDrawing = true;
        return extendedFile;
      });
      combinedFiles.push(...modifiedDrawingFiles);

      // Schematic Drawings (optional)
      if (schematicFiles.length > 0) {
        const modifiedFiles = schematicFiles.map((file) => {
          const extendedFile = file as File & { isSchematicDrawing?: boolean };
          extendedFile.isSchematicDrawing = true;
          return extendedFile;
        });
        combinedFiles.push(...modifiedFiles);
      }

      // Step 3: Upload files
      await uploadFiles(combinedFiles, createdPackageId);

      // Step 4: Start analysis
      await startAnalysis(createdPackageId, PipelineName.SCHEDULE_CHECK);
      toast.success(t('package-creation.success-analyzed'));
    } catch (error) {
      console.log('Error starting analysis:', error);
      toast.error(t('package-creation.failed-analyzed'));
    } finally {
      setIsProcessing(false);
    }
  }, [
    getValues,
    createDocumentPackage,
    type,
    uploadFiles,
    startAnalysis,
    t,
    scheduleFiles,
    drawingFiles,
    schematicFiles,
  ]);

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });

  const CreateScheduleCheckPackageForm = () => {
    return (
      <>
        <FormProvider {...methods}>
          <form
            className="w-full max-w-[90rem] -mx-4 px-4 sm:mx-0 sm:px-0"
            onSubmit={handleSubmit(onAnalyze)}
          >
            <div className="flex flex-col max-w-[50rem] mx-auto" translate="no">
              <IterationName
                validateName={validateName}
                label={t('package-creation.analysis-name')}
                errorMessage={`${t('package-creation.analysis-name-empty')}*`}
              />
            </div>
            <div className="max-w-[50rem] mx-auto">
              <SelectLanguage />
            </div>

            {/* Two columns layout for Schedule File and Drawings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              {/* Drawings Upload - MANDATORY */}
              <div className="flex flex-col">
                <div className="mb-2 h-10">
                  <p className="text-base font-semibold text-day-neutral-subtle">
                    {t('package-creation.drawings-label')}{' '}
                    <span className="text-day-error-default">*</span>
                  </p>
                  <span className="block text-xs text-gray-500">
                    {t('package-creation.drawings-hint')}
                  </span>
                </div>
                <FileUpload
                  files={drawingFiles}
                  setFiles={setDrawingFiles}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('package-creation.drawings-files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>

              {/* Schedule File Upload - OPTIONAL */}
              <div className="flex flex-col">
                <div className="mb-2 h-10">
                  <p className="text-base font-semibold text-day-neutral-subtle">
                    {t('package-creation.schedule-file-label')}
                  </p>
                  <span className="block text-xs text-gray-500">
                    {t('package-creation.schedule-file-hint')}
                  </span>
                </div>
                <FileUpload
                  files={scheduleFiles}
                  setFiles={setScheduleFiles}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('package-creation.schedule-file-files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>
            </div>

            {/* Schematic Drawings Upload - OPTIONAL - Full width below */}
            <div className="mt-6">
              <div className="mb-2">
                <p className="text-base font-semibold text-day-neutral-subtle">
                  {t('package-creation.schematic-drawings-label')}
                </p>
                <span className="block text-xs text-gray-500">
                  {t('package-creation.schematic-drawings-hint')}
                </span>
              </div>
              <FileUpload
                files={schematicFiles}
                setFiles={setSchematicFiles}
                dropAreaLabel={t('document-upload.drag-n-drop-here')}
                dropAreaClassName="mt-2 h-48 w-full"
                fileButtonLabel={t('document-upload.browse-files')}
                folderButtonLabel={t('document-upload.browse-folders')}
                uploadListLabel={t('package-creation.schematic-drawings-files')}
                zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              />
            </div>

            <div className="fc-w-full mt-6 max-w-[50rem] mx-auto">
              <WizardAnalyzeButton
                localFiles={[...scheduleFiles, ...drawingFiles, ...schematicFiles]}
                type="submit"
              />
            </div>
          </form>
        </FormProvider>
      </>
    );
  };

  const messageHeader = <ScheduleCheckUploadInfo />;

  return (
    <WizardLayout
      isProcessing={isProcessing}
      messageHeader={messageHeader}
      isUploading={isUploading}
      progress={progress}
      tabContent={{
        [TabType.CREATE_PACKAGE]: <CreateScheduleCheckPackageForm />,
      }}
    />
  );
};
